import { push } from 'connected-react-router';
import CoreAPIService from '../../../utils/core-api';
import ObjectUtils from '../../../utils/objectUtils';
import { fetchAction } from '../../actions';
import { updateDocument } from '../shared/document-utils';
import { types as documentTypes } from '../documents/actionTypes';

import { DispatchGroup, DriverById, Driver, DriverPoints, LightStructure } from '../api-types';
import { paths } from '../../../routes/paths';
export type { DriverById, DispatchGroup };

export const name: string = 'drivers';

export const DRIVER_POINTS_PER_PAGE = 25;

export const types = {
    EDIT_DRIVER: name + '/EDIT_DRIVER',
    GET_DRIVER: name + '/GET_DRIVER',
    GET_DRIVER_POINTS: name + '/GET_DRIVER_POINTS',
    RESET_DRIVER_PASSWORD: name + '/RESET_DRIVER_PASSWORD',
    GET_DISPATCH_GROUPS: name + '/GET_DISPATCH_GROUPS',
    GET_CONTROL_TOWERS: name + '/GET_CONTROL_TOWERS',
    SUSPEND_DRIVER: name + '/SUSPEND_DRIVER',
    UNSUSPEND_DRIVER: name + '/UNSUSPEND_DRIVER',
};

interface DriverState {
    drivers: DriverById;
    drivers_list: Partial<Driver>[];
    driver_points: { [driverId: string]: DriverPoints };
    dispatch_groups: DispatchGroup[];
    control_towers: LightStructure[];
}

const initialState: DriverState = {
    drivers: {} as DriverById,
    drivers_list: [],
    driver_points: {},
    dispatch_groups: [],
    control_towers: [],
};

const reducer = (state = initialState, action): DriverState => {
    const payload = action.payload;
    const parameters = action.parameters;
    const id = parameters ? parameters.id : 0;
    switch (action.type) {
        case types.GET_DRIVER:
        case types.EDIT_DRIVER:
            action = {
                ...action,
                payload: {
                    ...action.payload,
                    documents: ObjectUtils.keepIds(payload.documents),
                },
            };
            return {
                ...state,
                drivers: { ...state.drivers, [id]: { ...state.drivers[id], ...action.payload } },
            };
        case types.GET_DRIVER_POINTS: {
            // Pages after the first one extend the timeline instead of replacing it.
            const previous = state.driver_points[id];
            const operations =
                payload.page > 1 && previous ? [...previous.operations, ...payload.operations] : payload.operations;
            return {
                ...state,
                driver_points: { ...state.driver_points, [id]: { ...payload, operations } },
            };
        }
        case types.SUSPEND_DRIVER:
        case types.UNSUSPEND_DRIVER:
            return {
                ...state,
                drivers: {
                    ...state.drivers,
                    [id]: {
                        ...state.drivers[id],
                        details: payload.details,
                    },
                },
            };
        case types.GET_DISPATCH_GROUPS:
            return {
                ...state,
                dispatch_groups: payload,
            };
        case types.GET_CONTROL_TOWERS:
            return {
                ...state,
                control_towers: payload,
            };
        case documentTypes.UPSERT_DOC:
            return updateDocument('driver', 'drivers', state, payload);
        default:
            return state;
    }
};
export default reducer;

export const actions = {
    getDriver: (id) => fetchAction(CoreAPIService.get(`/core/drivers/${id}`), types.GET_DRIVER, { id }),
    getDriverPoints: (id, page: number = 1) =>
        fetchAction(
            CoreAPIService.get(`/core/drivers/${id}/points`, { page, num_per_page: DRIVER_POINTS_PER_PAGE }),
            types.GET_DRIVER_POINTS,
            { id },
        ),
    resetDriverPassword: (id, callback) =>
        fetchAction(
            CoreAPIService.put(`/core/drivers/${id}/password_reset`, {}),
            types.RESET_DRIVER_PASSWORD,
            { id },
            { success: callback },
        ),
    getDispatchGroups: () => fetchAction(CoreAPIService.get(`/core/dispatch_groups`), types.GET_DISPATCH_GROUPS, {}),
    getControlTowers: () => fetchAction(CoreAPIService.get(`/core/control_towers`), types.GET_CONTROL_TOWERS, {}),
    editDriver: (id, payload, callback?) =>
        fetchAction(
            CoreAPIService.put(`/core/drivers/${id}`, payload),
            types.EDIT_DRIVER,
            { id },
            {
                success: (dispatch) => {
                    dispatch(push(`${paths.drivers}/${id}`));
                    callback?.();
                },
            },
        ),
    suspendDriver: (id, reason, date, callback) =>
        fetchAction(
            CoreAPIService.post(`/core/drivers/${id}`, {
                details: {
                    prebookSuspendedUntilDate: new Date(date).getTime() / 1000,
                    prebookSuspendedUntilReason: reason,
                },
            }),
            types.SUSPEND_DRIVER,
            { id },
            { success: callback },
        ),
    unsuspendDriver: (id) =>
        fetchAction(
            CoreAPIService.post(`/core/drivers/${id}`, { details: { prebookSuspendedUntilDate: '' } }),
            types.UNSUSPEND_DRIVER,
            { id },
        ),
};

const localState = (state: any): DriverState => state[name] || initialState;
export const selectors = {
    drivers: (state) => localState(state).drivers,
    driversList: (state) => localState(state).drivers_list,
    driverPoints: (state) => localState(state).driver_points,
    dispatchGroup: (state) => localState(state).dispatch_groups,
    sortedDispatchGroup: (state) => localState(state).dispatch_groups.sort((a, b) => a.displayOrder - b.displayOrder),
    controlTowers: (state) => localState(state).control_towers,
};
